import React from 'react';
import { createRoot } from 'react-dom/client';
import { loadDesignSystem } from './ds';
import { RouterProvider } from './router';
import { I18nProvider } from './i18n';
import { applyTheme, savedTheme, watchSystemTheme } from './theme';
import { App } from './App';
import './styles.css';

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

const root = createRoot(container);

// `theme-boot.js` already set the attribute before the first paint. This keeps
// it in step with the system while the choice is `system`.
applyTheme(savedTheme());
watchSystemTheme();

/**
 * The service worker, and why it is registered last.
 *
 * It caches the shell and nothing else — `/api/*` never passes through it, for
 * the reasons in `public/sw.js`. Registration failing is not a reason to fail
 * the app: an insecure origin, a private window and a browser with workers
 * disabled all land here, and the app works without one.
 */
if ('serviceWorker' in navigator && window.isSecureContext) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Nothing to tell anybody. The app is not offline-capable this session.
    });
  });
}

// The design system bundle registers its components on a global and every page
// renders them, so mounting before it resolves would throw. Waiting here keeps
// every component free of load-state checks.
loadDesignSystem()
  .then(() => {
    root.render(
      <React.StrictMode>
        <RouterProvider>
          <I18nProvider>
            <App />
          </I18nProvider>
        </RouterProvider>
      </React.StrictMode>
    );
  })
  .catch((err) => {
    console.error(err);
    // Deliberately English, and the only copy here that is: this renders when
    // the design system bundle failed to load, which is before any provider
    // exists — there is no `t()` to call and no catalogue to call it against.
    root.render(
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          padding: 40,
          background: 'var(--app-ground)',
          fontFamily: 'Inter, sans-serif',
          textAlign: 'center'
        }}
      >
        <div>
          <h1 style={{ fontFamily: "'Baloo 2', sans-serif", fontSize: 28 }}>
            Something did not load.
          </h1>
          <p style={{ color: 'var(--text-muted)', maxWidth: 440 }}>
            The design system bundle could not be fetched. Reload the page, and if it keeps
            happening the deployment is missing <code>/_ds/mauvely/_ds_bundle.js</code>.
          </p>
        </div>
      </div>
    );
  });
